import { CheckCircleFilled } from "@ant-design/icons";
import { Avatar, Divider, List, Spin, Tag } from "antd";
import React, { useState } from "react";
import { getUserAvatar } from "~/components/profile/UserAvatar.jsx";
import { $t } from "~/i18n.js";
import { _parseDate } from "~/lib/localize.js";
import { colors } from "~/theme/colors.js";
import { FlightOpenIcon } from "../flight/FlightOpenIcon.jsx";
import { StackedBadge } from "./BadgeLevels.jsx";

export const BadgeDetail = ({
  config,
  earnedLevels,
  achievers,
  latestValue,
  loadingAchievers,
}) => {

  const maxLevel =
    Object.keys(earnedLevels).length > 0
      ? Math.max(...Object.keys(earnedLevels).map(Number))
      : 0;

  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const pagedAchievers = achievers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

      return (
    <div
      style={{
        margin: "0 0 12px",
        padding: "16px",
        borderRadius: 8,
        border: `1px solid ${colors.gray_light}`,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          gap: 16,
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <StackedBadge
          icon={config.icon}
          label={config.label}
          count={maxLevel || 1}
          earned={maxLevel > 0}
          size={64}
          step={10}
        />
        <div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{config.label}</div>
          <div style={{ fontSize: 13, color: "#555", marginTop: 2 }}>
            {config.description}
          </div>
        </div>
      </div>

      {/* Vertical step list */}
      <div style={{ paddingLeft: 24 }}>
        {config.levels.map((criteria, idx) => {
          const level = idx + 1;
          const earned = !!earnedLevels[level];
          const data = earnedLevels[level];
          const isLast = idx === config.levels.length - 1;
          const showProgress = latestValue && !earned && !config.levels.slice(0, idx).some((_, i) => !earnedLevels[i + 1]);
          return (
            <div
              key={level}
              style={{ position: "relative", paddingBottom: isLast ? 0 : 6 }}
            >
              {/* vertical line */}
              {!isLast && (
                <div
                  style={{
                    position: "absolute",
                    left: -15,
                    top: 13,
                    bottom: 0,
                    width: 2,
                    background: earned ? "#1677ff" : "#e0e0e0",
                  }}
                />
              )}
              {/* dot */}
              {earned && level === maxLevel ? (
                <CheckCircleFilled
                  style={{
                    position: "absolute",
                    left: -20.5,
                    top: 5,
                    fontSize: 13,
                    color: "#1677ff",
                  }}
                />
              ) : (
                <div
                  style={{
                    position: "absolute",
                    left: -19,
                    top: 4,
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: earned ? "#1677ff" : "#f0f0f0",
                    border: `2px solid ${earned ? "#1677ff" : "#bbb"}`,
                  }}
                />
              )}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    color: earned ? "#000" : "#aaa",
                    fontWeight: earned ? 500 : 400,
                  }}
                >
                  {criteria}
                </span>
                {showProgress && (
                  <Tag style={{ fontSize: 11, padding: "0 6px" }}>
                    {latestValue}
                  </Tag>
                )}
                {earned && data?.date_achieved && (
                  <Tag color="blue" style={{ fontSize: 11, padding: "0 6px" }}>
                    {_parseDate(data.date_achieved)}
                  </Tag>
                )}
                {earned && data?.flight_id && (
                  <FlightOpenIcon flight_uuid={data.flight_id} />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Achievers */}
      <Divider style={{ margin: "16px 0 8px" }}>
        <span className="gray small">{$t("Recent behaald door")}</span>
      </Divider>

      {loadingAchievers ? (
        <Spin size="small" />
      ) : achievers.length === 0 ? (
        <p style={{ fontSize: 12, color: "#aaa", margin: 0 }}>
          {$t("Nog niemand")}
        </p>
      ) : (
        <List
          size="small"
          dataSource={pagedAchievers}
          pagination={achievers.length > PAGE_SIZE ? {
            current: page,
            pageSize: PAGE_SIZE,
            total: achievers.length,
            onChange: setPage,
            size: "small",
          } : false}
          renderItem={(a, idx) => (
            <List.Item
              key={idx}
              extra={
                <StackedBadge
                  icon={config.icon}
                  label={config.label}
                  count={a.level}
                  earned
                  size={15}
                  step={5}
                />
              }
            >
              <List.Item.Meta
                avatar={
                  <Avatar
                    size={28}
                    src={getUserAvatar({ uuid: a.user_uuid }, true)}
                  />
                }
                title={
                  <p style={{ fontSize: 12, fontWeight: 500 }}>
                    {a.user_first_name} {a.user_last_name}
                    <Tag color="blue" style={{ fontSize: 11, marginLeft: 6 }}>
                      {_parseDate(a.date_achieved)}
                    </Tag>
                  </p>
                }
                description={
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    {a.flight_id && (
                      <FlightOpenIcon
                        flight_uuid={a.flight_id}
                        buttonProps={{ size: "small" }}
                      />
                    )}
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );
};
